'use client';
import React, { useRef } from 'react';
import styles from './SegmentedToggle.module.scss';

export interface SegmentedToggleOption<T extends string> {
    value: T;
    label: string;
}

interface SegmentedToggleProps<T extends string> {
    /** Accessible name of the group, e.g. "Country". */
    label: string;
    options: SegmentedToggleOption<T>[];
    value: T;
    onChange: (value: T) => void;
    /** Stretch the group across its container, with the options sharing the width equally. */
    fullWidth?: boolean;
}

function SegmentedToggle<T extends string>({label, options, value, onChange, fullWidth = false}: SegmentedToggleProps<T>) {
    const optionRefs = useRef<(HTMLButtonElement | null)[]>([]);
    const selectedIndex = options.findIndex(option => option.value === value);

    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        const step = (event.key === 'ArrowRight' || event.key === 'ArrowDown') ? 1
            : (event.key === 'ArrowLeft' || event.key === 'ArrowUp') ? -1
            : 0;
        if (step === 0) {
            return;
        }

        event.preventDefault();
        const nextIndex = (selectedIndex + step + options.length) % options.length;
        onChange(options[nextIndex].value);
        optionRefs.current[nextIndex]?.focus();
    };

    return (
        <div role="radiogroup" aria-label={label} onKeyDown={handleKeyDown}
             className={`${styles.segmentedToggle}${fullWidth ? ` ${styles.fullWidth}` : ''}`}>
            {options.map((option, index) => {
                const isSelected = option.value === value;
                return (
                    <button key={option.value} type="button" role="radio" aria-checked={isSelected}
                            tabIndex={isSelected ? 0 : -1}
                            ref={element => { optionRefs.current[index] = element; }}
                            onClick={() => onChange(option.value)}
                            className={`${styles.option}${isSelected ? ` ${styles.selected}` : ''}`}>
                        {option.label}
                    </button>
                );
            })}
        </div>
    );
}

export default SegmentedToggle;
